import React from "react";
import ReactDOM from "react-dom";
import { Poll, PollCommonProps, transformPollTargets } from "../poll";
import { usePollContext } from "../../stores/poll";
import { usePollResults, useVoteWithAnalytics } from "../poll";
import { useSnowplowContext } from "../../analytics/snowplow";

import styles from "./styles.modules.scss";
import {
    dispatchTabVisiblityEvent,
    dispatchControlButtonVisibilityEvent,
} from "../../utils/event";

export const PollModal = ({
    isOpen,
    pollId,
    options,
    pollTargets,
    ...viewProps
}: PollCommonProps & {
    isOpen: boolean;
    onClose: () => void;
}) => {
    const {
        state: { voteCache, votingState, polls },
        canVote,
        isClosed,
        actions: { refreshPollInfo, voteFromModal: vote },
    } = usePollContext();

    // Transform poll targets into options before calling usePollResults
    const pollOptions =
        pollTargets && pollTargets.length > 0
            ? transformPollTargets(pollTargets)
            : options;

    const { optionsWithResults, showResults } = usePollResults(
        pollId,
        pollOptions,
    );
    const { trackImpression } = useSnowplowContext();
    const voteWithAnalytics = useVoteWithAnalytics(pollId, pollOptions, vote);

    React.useEffect(() => {
        if (isOpen) {
            refreshPollInfo(pollId);
            dispatchTabVisiblityEvent(false);
            dispatchControlButtonVisibilityEvent(false);
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "initial";
            dispatchTabVisiblityEvent(true);
            dispatchControlButtonVisibilityEvent(true);
        }
    }, [isOpen]);

    React.useEffect(() => {
        if (isOpen) {
            // Poll impressions are have special prefix to distinguish them from the poll carousel impression
            trackImpression(`poll-${pollId}`, viewProps.pollTitle);
        }
    }, [pollId, isOpen]);

    if (!isOpen) {
        return null;
    }

    const modalRoot = document.getElementById("modal-root");

    if (!modalRoot) {
        return null;
    }

    const contents = (
        <Poll
            userVote={voteCache.votes[pollId] ?? null}
            canVote={canVote(pollId) && polls[pollId]?.status !== "closed"}
            isClosed={isClosed(pollId)}
            vote={voteWithAnalytics}
            options={optionsWithResults}
            showResults={showResults}
            isError={votingState === "fail"}
            isVoting={votingState === "voting"}
            extraClassNames={[String(styles.modal)]}
            pollTargets={pollTargets}
            {...viewProps}
        />
    );

    return ReactDOM.createPortal(contents, modalRoot);
};
